export const SELECTION_PANEL_GAP_PX = 40;
export const SELECTION_PANEL_VIEWPORT_MARGIN_PX = 8;
export const SELECTION_PANEL_BOTTOM_RESERVE_PX = 96;

export type SelectionPanelSide = "top" | "bottom" | "left" | "right";

export interface PanelRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface PanelSize {
  readonly width: number;
  readonly height: number;
}

export interface SelectionPanelPlacementInput {
  readonly anchor: PanelRect;
  readonly panel: PanelSize;
  readonly viewport: PanelSize;
}

export interface SelectionPanelPlacement {
  readonly side: SelectionPanelSide;
  readonly left: number;
  readonly top: number;
}

function clamp(value: number, lower: number, upper: number): number {
  return Math.min(Math.max(value, lower), upper);
}

export function resolveSelectionPanelSafeArea(viewport: PanelSize): PanelRect {
  const width = viewport.width - SELECTION_PANEL_VIEWPORT_MARGIN_PX * 2;
  const height =
    viewport.height - SELECTION_PANEL_VIEWPORT_MARGIN_PX - SELECTION_PANEL_BOTTOM_RESERVE_PX;

  // 極端に小さい表示領域では予約領域を適用せず、有限な座標を返せる範囲を優先する。
  if (width <= 0 || height <= 0) {
    return {
      left: 0,
      top: 0,
      width: Math.max(0, viewport.width),
      height: Math.max(0, viewport.height),
    };
  }

  return {
    left: SELECTION_PANEL_VIEWPORT_MARGIN_PX,
    top: SELECTION_PANEL_VIEWPORT_MARGIN_PX,
    width,
    height,
  };
}

export function resolveSelectionPanelPlacement(
  input: SelectionPanelPlacementInput
): SelectionPanelPlacement {
  const safeArea = resolveSelectionPanelSafeArea(input.viewport);
  const safeRight = safeArea.left + safeArea.width;
  const safeBottom = safeArea.top + safeArea.height;
  const anchorRight = input.anchor.left + input.anchor.width;
  const anchorBottom = input.anchor.top + input.anchor.height;
  const clippedLeft = Math.max(input.anchor.left, safeArea.left);
  const clippedTop = Math.max(input.anchor.top, safeArea.top);
  const clippedRight = Math.min(anchorRight, safeRight);
  const clippedBottom = Math.min(anchorBottom, safeBottom);

  const maxLeft = Math.max(safeArea.left, safeRight - input.panel.width);
  const maxTop = Math.max(safeArea.top, safeBottom - input.panel.height);
  const clampLeft = (value: number): number => clamp(value, safeArea.left, maxLeft);
  const clampTop = (value: number): number => clamp(value, safeArea.top, maxTop);

  if (clippedRight <= clippedLeft || clippedBottom <= clippedTop) {
    return {
      side: "bottom",
      left: clampLeft(safeArea.left + (safeArea.width - input.panel.width) / 2),
      top: clampTop(safeBottom - input.panel.height),
    };
  }

  const clippedAnchor: PanelRect = {
    left: clippedLeft,
    top: clippedTop,
    width: clippedRight - clippedLeft,
    height: clippedBottom - clippedTop,
  };
  const available: Record<SelectionPanelSide, number> = {
    top: clippedAnchor.top - safeArea.top,
    bottom: safeBottom - (clippedAnchor.top + clippedAnchor.height),
    left: clippedAnchor.left - safeArea.left,
    right: safeRight - (clippedAnchor.left + clippedAnchor.width),
  };
  const required: Record<SelectionPanelSide, number> = {
    top: input.panel.height + SELECTION_PANEL_GAP_PX,
    bottom: input.panel.height + SELECTION_PANEL_GAP_PX,
    left: input.panel.width + SELECTION_PANEL_GAP_PX,
    right: input.panel.width + SELECTION_PANEL_GAP_PX,
  };
  const sides: readonly SelectionPanelSide[] = ["top", "bottom", "left", "right"];
  const fittingSides = sides.filter((side) => available[side] >= required[side]);
  const candidates = fittingSides.length > 0 ? fittingSides : sides;
  // 同値では先頭を維持し、top → bottom → left → right の優先順を保つ。
  const side = candidates.reduce((best, candidate) =>
    available[candidate] > available[best] ? candidate : best
  );
  const centerX = clippedAnchor.left + clippedAnchor.width / 2;
  const centerY = clippedAnchor.top + clippedAnchor.height / 2;

  switch (side) {
    case "top":
      return {
        side,
        left: clampLeft(centerX - input.panel.width / 2),
        top: clampTop(clippedAnchor.top - SELECTION_PANEL_GAP_PX - input.panel.height),
      };
    case "bottom":
      return {
        side,
        left: clampLeft(centerX - input.panel.width / 2),
        top: clampTop(clippedAnchor.top + clippedAnchor.height + SELECTION_PANEL_GAP_PX),
      };
    case "left":
      return {
        side,
        left: clampLeft(clippedAnchor.left - SELECTION_PANEL_GAP_PX - input.panel.width),
        top: clampTop(centerY - input.panel.height / 2),
      };
    case "right":
      return {
        side,
        left: clampLeft(clippedAnchor.left + clippedAnchor.width + SELECTION_PANEL_GAP_PX),
        top: clampTop(centerY - input.panel.height / 2),
      };
  }
}
